import { axiosPrivate } from "../api/axios";
import { useEffect, useRef } from "react";
import useRefreshToken from "./useRefreshToken";
import useAuth from "./useAuth";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

let globalCsrfToken = null;

const useAxiosPrivate = () => {
    const refresh = useRefreshToken();
    const { auth, setAuth } = useAuth();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const refreshRef = useRef(refresh);
    const setAuthRef = useRef(setAuth);
    const navigateRef = useRef(navigate);
    const enqueueSnackbarRef = useRef(enqueueSnackbar);

    useEffect(() => {
        refreshRef.current = refresh;
        setAuthRef.current = setAuth;
        navigateRef.current = navigate;
        enqueueSnackbarRef.current = enqueueSnackbar;
    });

    useEffect(() => {
        const requestIntercept = axiosPrivate.interceptors.request.use(
            config => {
                const storedAuth = JSON.parse(localStorage.getItem('auth') || '{}');
                const currentAuth = storedAuth?.accessToken ? storedAuth : auth;

                if (!config.headers['Authorization'] && currentAuth?.accessToken) {
                    config.headers['Authorization'] = `Bearer ${currentAuth.accessToken}`;
                }

                const currentLang = localStorage.getItem('i18nextLng') || 'lng1';
                config.headers['X-Language'] = currentLang;

                if (config.method === 'post' ||
                    config.method === 'put' ||
                    config.method === 'delete' ||
                    config.method === 'patch') {

                    if (globalCsrfToken) {
                        config.headers['X-CSRF-TOKEN'] = globalCsrfToken;
                    }
                }

                return config;
            }, (error) => Promise.reject(error)
        );

        const responseIntercept = axiosPrivate.interceptors.response.use(
            response => {
                const csrfToken = response.headers['x-csrf-token'] || response.headers['X-CSRF-TOKEN'];
                if (csrfToken) {
                    globalCsrfToken = csrfToken;
                }
                return response;
            },
            async (error) => {
                const prevRequest = error?.config;
                const errorCode = error?.response?.data?.error;
                const errorMessage = error?.response?.data?.message;

                if (error?.response?.status === 401 && !prevRequest?.sent) {
                    prevRequest.sent = true;
                    try {
                        const newAccessToken = await refreshRef.current();

                        if (!newAccessToken) {
                            const isLoginPage = window.location.pathname === '/' || window.location.pathname === '/login';
                            if (!isLoginPage) {
                                setAuthRef.current({});
                                localStorage.removeItem('auth');
                                navigateRef.current('/');
                                enqueueSnackbarRef.current('Your session has expired. Please log in again.', { variant: 'warning' });
                            }
                            return Promise.reject(error);
                        }

                        prevRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                        return axiosPrivate(prevRequest);
                    } catch (refreshError) {
                        const isLoginPage = window.location.pathname === '/' || window.location.pathname === '/login';
                        if (!isLoginPage) {
                            setAuthRef.current({});
                            localStorage.removeItem('auth');
                            navigateRef.current('/');
                            enqueueSnackbarRef.current('Your session has expired. Please log in again.', { variant: 'warning' });
                        }
                        return Promise.reject(refreshError);
                    }
                }
                else if (error?.response?.status === 403) {
                    enqueueSnackbarRef.current(errorMessage || 'You don\'t have permission to perform this action.', { variant: 'error' });
                }
                else if (error?.response?.status === 404) {
                    console.warn('Resource not found:', error?.config?.url);
                }
                else if (error?.response?.status === 409) {
                    enqueueSnackbarRef.current(errorMessage || 'Conflict with the current state of the resource.', { variant: 'warning' });
                }
                else if (error?.response?.status === 400) {
                    if (errorMessage) {
                        enqueueSnackbarRef.current(errorMessage, { variant: 'error' });
                    } else {
                        enqueueSnackbarRef.current('Invalid request. Please check your input.', { variant: 'error' });
                    }
                }
                else if (error?.response?.status >= 500) {
                    enqueueSnackbarRef.current('Server error. Please try again later.', { variant: 'error' });
                    console.error('Server error:', error);
                }
                else if (!error?.response && error?.request) {
                    if (error?.request?.status !== 0) {
                        enqueueSnackbarRef.current('Network error. Please check your connection.', { variant: 'error' });
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => {
            axiosPrivate.interceptors.request.eject(requestIntercept);
            axiosPrivate.interceptors.response.eject(responseIntercept);
        }
        // eslint-disable-next-line
    }, []);

    return axiosPrivate;
}

export default useAxiosPrivate;
